/**
 * aiProxy.js — Thin wrapper around Firebase Callable Functions.
 *
 * The client calls these instead of Gemini directly when using
 * platform credits. Each function returns the AI response plus
 * credit usage info.
 *
 * When in BYOK mode, the client bypasses this module entirely
 * and calls Gemini directly (existing behavior).
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

// Cache function references
let _chatFn = null;
let _voiceDumpFn = null;
let _balanceFn = null;
let _circuitGenFn = null;

function getChatFn() {
  if (!_chatFn) _chatFn = httpsCallable(functions, 'proxyGeminiChat', { timeout: 60000 });
  return _chatFn;
}

function getVoiceDumpFn() {
  if (!_voiceDumpFn) _voiceDumpFn = httpsCallable(functions, 'proxyGeminiVoiceDump', { timeout: 120000 });
  return _voiceDumpFn;
}

function getBalanceFn() {
  if (!_balanceFn) _balanceFn = httpsCallable(functions, 'getCreditBalance', { timeout: 10000 });
  return _balanceFn;
}

function getCircuitGenFn() {
  if (!_circuitGenFn) _circuitGenFn = httpsCallable(functions, 'proxyGeminiCircuitGen', { timeout: 120000 });
  return _circuitGenFn;
}

/**
 * Send an AI Coach message through the server proxy.
 * @param {Object} params
 * @param {string} params.message
 * @param {Array} params.conversationHistory
 * @param {string} params.dataContext
 * @param {'coach'|'extract'|'triage'} [params.promptMode] — server-side prompt selector (default 'coach').
 *   The client NEVER sends a raw system prompt; the function picks it by mode.
 * @param {string} [params.tonePreset] — tone PRESET KEY only (S9). Omitted when undefined.
 * @param {string} [params.modelTier] — model tier KEY only; resolved server-side. Omitted when undefined.
 * @returns {{ content: string, creditsUsed: number, creditsRemaining: number }}
 */
export async function proxyChatWithCoach({ message, conversationHistory = [], dataContext = '', promptMode = 'coach', tonePreset, modelTier }) {
  const fn = getChatFn();
  // Omit undefined optionals so old callers behave EXACTLY as before (the
  // Firebase serializer would otherwise encode undefined→null; the server
  // treats absent keys as "use current behavior").
  const payload = { message, conversationHistory, dataContext, promptMode };
  if (tonePreset !== undefined) payload.tonePreset = tonePreset;
  if (modelTier !== undefined) payload.modelTier = modelTier;
  const result = await fn(payload);
  return result.data;
}

/**
 * Process a voice dump through the server proxy.
 * @param {Object} params
 * @param {string} params.audioBase64 — Base64-encoded audio
 * @param {string} params.mimeType
 * @param {string} params.dataContext
 * @param {string} params.templatePrompt
 * @param {string} params.systemPrompt
 * @param {string} [params.modelTier] — model tier KEY only; resolved server-side. Omitted when undefined.
 * @returns {{ transcript, actions, summary, coachingNudge, creditsUsed, creditsRemaining }}
 */
export async function proxyVoiceDump({ audioBase64, mimeType, dataContext, templatePrompt, systemPrompt, modelTier }) {
  const fn = getVoiceDumpFn();
  const payload = { audioBase64, mimeType, dataContext, templatePrompt, systemPrompt };
  if (modelTier !== undefined) payload.modelTier = modelTier;
  const result = await fn(payload);
  return result.data;
}

/**
 * Fetch the user's current credit balance.
 * Used as a fallback when the real-time listener hasn't loaded yet.
 * @returns {{ credits, monthlyAllocation, tier, totalUsed, nextResetAt, byokEnabled }}
 */
export async function fetchCreditBalance() {
  const fn = getBalanceFn();
  const result = await fn({});
  return result.data;
}

/**
 * Generate a Circuit draft from multi-modal input.
 * @param {Object} params { mode, text?, audioBase64?, mimeType?, selection?, dataContext?, timeBudgetSeconds?, modelTier? }
 * @returns {{ draft, transcript?, creditsUsed, creditsRemaining }}
 */
export async function proxyCircuitGen({ mode = 'text', text, audioBase64, mimeType, selection, dataContext, timeBudgetSeconds, modelTier } = {}) {
  const fn = getCircuitGenFn();
  // Forward ONLY defined fields. The Firebase callable serializer encodes
  // `undefined` as `null`, and the server's parameter defaults (e.g.
  // `selection = {}`) only apply to absent/undefined keys — a forwarded `null`
  // slips past them and crashes the prompt builder on `selection.projectName`.
  // Omitting undefined lets the server defaults take effect.
  const payload = { mode };
  if (text !== undefined) payload.text = text;
  if (audioBase64 !== undefined) payload.audioBase64 = audioBase64;
  if (mimeType !== undefined) payload.mimeType = mimeType;
  if (selection !== undefined) payload.selection = selection;
  if (dataContext !== undefined) payload.dataContext = dataContext;
  if (timeBudgetSeconds !== undefined) payload.timeBudgetSeconds = timeBudgetSeconds;
  if (modelTier !== undefined) payload.modelTier = modelTier;
  const result = await fn(payload);
  return result.data;
}
